/**
 * Smart logger for App-wide Mercure subscriptions
 * Batches subscription logs to reduce console noise
 */
const isDev = import.meta.env?.DEV === true;

const mercureAppLog = (message, data = null, type = "info") => {
  if (!isDev) return;

  const prefix = `%c AppMercure `;
  const baseStyle = "padding: 2px 4px; border-radius: 3px; font-weight: bold; color: white;";

  const styles = {
    info: `${baseStyle} background: #9b59b6;`,
    success: `${baseStyle} background: #2ecc71;`,
    warn: `${baseStyle} background: #f1c40f; color: #333;`,
    error: `${baseStyle} background: #e74c3c;`,
  };

  const args = [prefix, styles[type], message];
  if (data !== null) args.push(data);

  console[type === "success" ? "log" : type === "info" ? "log" : type](...args);
};

/**
 * Log batch subscription to multiple topics
 * @param {string[]} topics - Array of topic paths
 * @param {number} userId - Current user ID
 */
export const logBatchSubscribe = (topics, userId) => {
  if (!topics || topics.length === 0) return;
  mercureAppLog(
    `Subscribed to ${topics.length} app topic${topics.length > 1 ? "s" : ""}`,
    { userId, topics },
    "success",
  );
};

/**
 * Log batch unsubscription from multiple topics
 * @param {number} topicCount - Number of topics unsubscribed
 * @param {number} userId - Current user ID
 */
export const logBatchUnsubscribe = (topicCount, userId) => {
  if (topicCount === 0) return;
  mercureAppLog(
    `Unsubscribed from ${topicCount} app topic${topicCount > 1 ? "s" : ""}`,
    { userId },
    "warn",
  );
};

/**
 * Log subscription readiness
 * @param {number} topicCount - Number of active topics
 * @param {number} userId - Current user ID
 */
export const logReady = (topicCount, userId) => {
  mercureAppLog(`✅ All app subscriptions ready (${topicCount} topics)`, { userId }, "success");
};

/**
 * Log errors
 * @param {string} context - Error context
 * @param {Error} error - Error object
 * @param {number} userId - Current user ID
 */
export const logError = (context, error, userId) => {
  mercureAppLog(`❌ ${context}`, { userId, error: error?.message }, "error");
};

export { mercureAppLog };
export default mercureAppLog;
